import { useEffect, useState } from 'react';
import Modal from '../../../components/common/Modal/Modal';
import Icon from '../../../components/common/Icon/Icon';
import { IconKeys } from '../../../components/common/Icon/IconKeys';
import { Button } from '../../../components/ui/Button/Button';
import CloseTripForm, { getSuggestedEndAt, getTripLabel } from '../../../components/CloseTripForm/CloseTripForm';
import useGetRole from '../../../API/hooks/useGetRole';
import useGetLongOngoingTrips from '../../../API/hooks/trips/useGetLongOngoingTrips';
import { LongOngoingTrip } from '../../../API/types/Trips';
import { getFormattedDateAndTime, getTimeZone, getTripDuration } from '../../../utils/dateUtils';
import S from './CloseOngoingTripsStyles';

type Props = {
  fleetId: string;
};

/** How often the "current time" the suggestions are validated against is refreshed while open. */
const NOW_REFRESH_INTERVAL_MS = 30000;

/** Renders the backend's threshold as something readable, e.g. "24 hours" or "3 days". */
const getThresholdLabel = (thresholdMs: number): string => {
  const hours = Math.round(thresholdMs / (60 * 60 * 1000));
  if (hours >= 48 && hours % 24 === 0) return `${hours / 24} days`;
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
};

function CloseOngoingTrips({ fleetId }: Props): JSX.Element | null {
  const { isAdmin, isFleetAdmin, isFleetManager } = useGetRole();
  const canCloseOngoingTrips = isAdmin || isFleetAdmin || isFleetManager;

  const [isOpen, setOpen] = useState(false);
  // Kept as a snapshot rather than an id: the list is refreshed after each close, and the trip
  // being closed drops out of it.
  const [selectedTrip, setSelectedTrip] = useState<LongOngoingTrip | null>(null);
  const [now, setNow] = useState<Date>(() => new Date());

  const {
    trips, thresholdMs, isLoading, isError,
  } = useGetLongOngoingTrips(fleetId, canCloseOngoingTrips && isOpen);

  useEffect(() => {
    if (!isOpen) return undefined;
    const intervalId = window.setInterval(() => setNow(new Date()), NOW_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [isOpen]);

  const resetState = (): void => {
    setSelectedTrip(null);
    setNow(new Date());
  };

  const handleOpen = (): void => {
    resetState();
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
    resetState();
  };

  const handleBackToList = (): void => setSelectedTrip(null);

  if (!canCloseOngoingTrips) {
    return null;
  }

  const hasTrips = trips.length > 0;
  // 0 until the first response lands, so the copy below stays hidden rather than naming a
  // threshold the backend has not confirmed yet.
  const thresholdLabel = thresholdMs ? getThresholdLabel(thresholdMs) : '';

  return (
    <S.Wrapper>
      <Button
        className="close-ongoing-trips"
        height="26px"
        borderRadius="100px"
        padding="0 12px"
        gap="8px"
        onClick={handleOpen}
      >
        <Icon iconKey={IconKeys.TIME} />
        <p>Close ongoing trips</p>
      </Button>
      {isOpen && (
        <Modal
          title="Close ongoing trips"
          state={isOpen}
          settings={{ minWidth: '760px', maxWidth: '860px' }}
          toggleState={setOpen}
          onClose={resetState}
        >
          <S.ModalContent>
            {!selectedTrip && (
              <>
                {!!thresholdLabel && (
                  <p className="description">
                    {`Here you can close trips that have been ongoing for more than ${thresholdLabel}. `}
                    Such trips usually mean the trip end was never reported by the device. Pick a
                    trip to close and provide the date and time it should be ended at.
                  </p>
                )}

                {isLoading && <p className="message">Loading ongoing trips...</p>}
                {isError && !isLoading && (
                  <p className="message error">Could not load ongoing trips. Please try again.</p>
                )}
                {!isLoading && !isError && !hasTrips && !!thresholdLabel && (
                  <p className="message">
                    {`None of the ongoing trips has been running for longer than ${thresholdLabel}, `}
                    so closing them manually is not available.
                  </p>
                )}

                {!isLoading && !isError && hasTrips && (
                  <ul className="trips">
                    {trips.map((trip) => {
                      const suggestion = getSuggestedEndAt(trip, now);
                      return (
                        <li key={trip._id}>
                          <div className="trip-row">
                            <span className="info">
                              <span className="primary">{getTripLabel(trip)}</span>
                              <span className="secondary">
                                {`Started ${getFormattedDateAndTime(trip.startAt)}`}
                                {` (${getTimeZone(trip.startAt)}) - ongoing for `}
                                <span className="duration">{getTripDuration(trip.startAt, null)}</span>
                              </span>
                              {!!suggestion && (
                                <span className="suggestion">
                                  {'Next trip on this vehicle started '}
                                  {getFormattedDateAndTime(suggestion, true)}
                                </span>
                              )}
                            </span>
                            <Button
                              padding="6px 20px"
                              onClick={() => setSelectedTrip(trip)}
                            >
                              Close
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}

                <div className="actions">
                  <Button padding="7px 40px" onClick={handleClose}>Close</Button>
                </div>
              </>
            )}

            {!!selectedTrip && (
              <CloseTripForm
                fleetId={fleetId}
                trip={selectedTrip}
                cancelLabel="Back"
                onCancel={handleBackToList}
                onClosed={handleBackToList}
              />
            )}
          </S.ModalContent>
        </Modal>
      )}
    </S.Wrapper>
  );
}

export default CloseOngoingTrips;
